/**
 * Generic image-zoom DOM handler — swaps an existing <img>/<picture> for a zoomable <canvas>
 * (backed by libraries/imageZoom.js), wires optional zoom-in/zoom-out buttons to it, keeps the
 * canvas sized to its container via ResizeObserver, and blocks the canvas's own pointer/touch/
 * drag events from bubbling to a parent carousel's drag-to-scroll listeners. Falls back to
 * leaving the original image untouched and visible if the image fails to load.
 *
 * Framework/feature-agnostic on purpose — any feature that needs "click +/- or pinch to zoom a
 * static image" can call this directly; it knows nothing about Redux, RxJS, or any one feature's
 * plugin host. Mirrors the split GlobalResources14/N1/js/lightbox/lightboxZoom.js has with
 * GlobalResources14/Default/js/modules/imageZoom.js, minus that lightbox's coupling to its own
 * pub-sub lightbox state — this handler is instantiated once per image, not once per gallery
 * with an internal "active slide" concept.
 *
 * @module handlers/imageZoomHandler
 */
import ImageZoom from 'libraries/imageZoom';

/**
 * Prevents a parent carousel's delegated drag listeners from receiving pointer/touch events
 * that originate on the zoom canvas, so panning/pinching the image doesn't also drag the
 * carousel.
 * @param {HTMLCanvasElement} canvas
 */
function blockGesturePropagation(canvas) {
  const stop = (event) => event.stopPropagation();
  canvas.addEventListener('touchstart', stop, { passive: true });
  canvas.addEventListener('mousedown', stop);
  canvas.addEventListener('pointerdown', stop);
  canvas.addEventListener('dragstart', stop);
}

/**
 * Reads the container's size on the next animation frame, so callers get committed layout
 * dimensions even when the container was just inserted into the DOM in the same tick.
 * @param {HTMLElement} container
 * @returns {Promise<{width: number, height: number}>}
 */
function measureContainer(container) {
  return new Promise((resolve) => {
    requestAnimationFrame(() => {
      const rect = container.getBoundingClientRect();
      resolve({ width: rect.width, height: rect.height });
    });
  });
}

/**
 * @param {Object} params
 * @param {HTMLElement} params.container - Positioned element (`position: relative` or similar)
 *   the canvas is prepended into, and whose size the canvas is kept in sync with.
 * @param {HTMLElement} params.imageElement - The existing `<img>` to hide once the canvas takes
 *   over, and to restore if the image fails to load or on destroy().
 * @param {string} params.imageUrl - URL of the image to load into the zoomable canvas.
 * @param {HTMLButtonElement} [params.zoomInButton] - Clicking it calls zoomIn().
 * @param {HTMLButtonElement} [params.zoomOutButton] - Clicking it calls zoomOut().
 * @param {HTMLElement} [params.controlsElement] - Shown (its `hidden` attribute removed) once
 *   the image finishes loading into the canvas; hidden again on error or destroy(). Pass the
 *   common ancestor of zoomInButton/zoomOutButton when they share one wrapper.
 * @param {string} [params.canvasClass] - Class name(s) applied to the created `<canvas>`.
 * @param {number} [params.maxZoomLevel] - Passed through to ImageZoom.
 * @param {number} [params.scaleFactor] - Passed through to ImageZoom.
 * @param {string} [params.zoomOutModeClass] - Passed through to ImageZoom.
 * @returns {{ destroy: Function }}
 */
export default function createImageZoomHandler({
  container,
  imageElement,
  imageUrl,
  zoomInButton,
  zoomOutButton,
  controlsElement,
  canvasClass,
  maxZoomLevel,
  scaleFactor,
  zoomOutModeClass,
} = {}) {
  if (!container || !imageElement || !imageUrl) {
    return { destroy() {} };
  }

  const state = {
    canvas: null,
    imageZoom: null,
    resizeObserver: null,
    destroyed: false,
  };

  function showControls() {
    controlsElement?.removeAttribute('hidden');
  }

  function hideControls() {
    controlsElement?.setAttribute('hidden', '');
  }

  function onZoomInClick() {
    state.imageZoom?.zoomIn();
  }

  function onZoomOutClick() {
    state.imageZoom?.zoomOut();
  }

  /**
   * Disables zoom in/out buttons at the respective bound so a click at the limit can't fire,
   * and re-enables them once the level moves away from that bound.
   * @param {{ atMin: boolean, atMax: boolean }} params
   */
  function onZoomLevelChange({ atMin, atMax }) {
    if (zoomInButton) {
      zoomInButton.disabled = atMax;
    }
    if (zoomOutButton) {
      zoomOutButton.disabled = atMin;
    }
  }

  function teardownCanvas() {
    if (state.resizeObserver) {
      state.resizeObserver.disconnect();
      state.resizeObserver = null;
    }
    if (state.canvas?.parentElement) {
      state.canvas.parentElement.removeChild(state.canvas);
    }
    state.imageZoom?.destroy();
    state.imageZoom = null;
    state.canvas = null;
    imageElement.style.display = '';
    hideControls();
    if (zoomInButton) {
      zoomInButton.disabled = false;
    }
    if (zoomOutButton) {
      zoomOutButton.disabled = false;
    }
  }

  function onLoad() {
    if (state.destroyed) {
      return;
    }
    imageElement.style.display = 'none';
    state.canvas.hidden = false;
    showControls();
  }

  function onError() {
    if (state.destroyed) {
      return;
    }
    teardownCanvas();
  }

  function onResize() {
    if (state.destroyed || !state.imageZoom) {
      return;
    }
    const rect = container.getBoundingClientRect();
    state.imageZoom.resize(rect.width, rect.height);
  }

  zoomInButton?.addEventListener('click', onZoomInClick);
  zoomOutButton?.addEventListener('click', onZoomOutClick);
  hideControls();

  measureContainer(container).then(({ width, height }) => {
    if (state.destroyed) {
      return;
    }

    const canvas = document.createElement('canvas');
    if (canvasClass) {
      canvas.className = canvasClass;
    }
    canvas.hidden = true;
    canvas.width = Math.round(width);
    canvas.height = Math.round(height);
    blockGesturePropagation(canvas);
    container.prepend(canvas);
    state.canvas = canvas;

    state.imageZoom = new ImageZoom(canvas, imageUrl, {
      maxZoomLevel,
      scaleFactor,
      zoomOutModeClass,
      onZoomChange: onZoomLevelChange,
    });
    state.imageZoom.picture.addEventListener('load', onLoad);
    state.imageZoom.picture.addEventListener('error', onError);

    if (typeof ResizeObserver === 'function') {
      state.resizeObserver = new ResizeObserver(onResize);
      state.resizeObserver.observe(container);
    } else {
      window.addEventListener('resize', onResize);
    }
  });

  return {
    /**
     * Detaches all listeners/observers, removes the canvas, and restores the original image.
     * Not called by every consumer (see floorPlanCarousel.dom.js's Task 6 comment on why it's
     * safe to skip there) — provided for consumers that manage a longer-lived container.
     */
    destroy() {
      state.destroyed = true;
      zoomInButton?.removeEventListener('click', onZoomInClick);
      zoomOutButton?.removeEventListener('click', onZoomOutClick);
      if (!state.resizeObserver) {
        window.removeEventListener('resize', onResize);
      }
      teardownCanvas();
    },
  };
}
